import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import supabaseClient from '../services/supabase';
import { Session } from '@supabase/supabase-js';
import { IS_DEV } from '../utils/misc';
import { getProfile } from '../services/profile';

/* ------------------------------------------------------------------ */
export type SessionStatus =
  | 'signed_out'
  | 'signed_in_no_profile'
  | 'signed_in_with_profile';

export interface AuthContextShape {
  session: Session | null;
  sessionStatus: SessionStatus;
  isLoading: boolean;
  authError: Error | null;
  profile: any;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextShape | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

/* ================================================================== */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  /* ---------------- base state ---------------- */
  const [session, setSession] = useState<Session | null>(null);
  const [sessionStatus, setSessionStatus] =
    useState<SessionStatus>('signed_out');
  const [profile, setProfile] = useState<any>(null);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /* remembers which email's profile we already fetched */
  const hasFetchedProfile = useRef<string | null>(null);

  /* ---------------- helper: load profile ---------------- */
  const loadProfileByEmail = useCallback(async (email: string) => {
    if (hasFetchedProfile.current === email) return; // already done

    setIsLoading(true);
    try {
      const profileData = await getProfile(email);
      setProfile(profileData);

      const complete =
        profileData &&
        ((profileData.class_year && profileData.class_year !== 0) ||
          (profileData.position !== undefined && profileData.position !== null));

      setSessionStatus(
        complete ? 'signed_in_with_profile' : 'signed_in_no_profile',
      );
      hasFetchedProfile.current = email;
    } catch (err) {
      setProfile(null);
      setSessionStatus('signed_in_no_profile');
      setAuthError(err instanceof Error ? err : new Error('Profile fetch failed'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* ---------------- Supabase listener ------------------- */
  useEffect(() => {
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (IS_DEV) console.log('[AuthProvider] auth event:', event, currentSession);

        // Check for .edu email on sign in or initial session
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          const email = currentSession?.user?.email;
          if (email && !email.endsWith('.edu')) {
            setSessionStatus('signed_out');
            setSession(null);
            setProfile(null);
            setAuthError(new Error('Only .edu email addresses are allowed'));
            setIsLoading(false);
            await supabaseClient.auth.signOut();
            window.location.href = '/';
            return;
          }
        }

        setSession(currentSession);

        if (event === 'SIGNED_OUT') {
          setProfile(null);
          setSessionStatus('signed_out');
          hasFetchedProfile.current = null;
          setIsLoading(false);
          return;
        }

        if (event === 'INITIAL_SESSION') {
          if (currentSession?.user?.email) {
            // Double check .edu email on initial session load
            if (!currentSession.user.email.endsWith('.edu')) {
              setSessionStatus('signed_out');
              setSession(null);
              setProfile(null);
              setAuthError(new Error('Only .edu email addresses are allowed'));
              setIsLoading(false);
              await supabaseClient.auth.signOut();
              window.location.href = '/';
              return;
            }
            await loadProfileByEmail(currentSession.user.email);
          } else {
            // no session at all
            setIsLoading(false);
            setSessionStatus('signed_out');
          }
        }

        /* NOTE: we deliberately ignore SIGNED_IN here
           to avoid double‑fetching; INITIAL_SESSION will
           handle the actual profile load when it fires. */
      },
    );

    return () => subscription.unsubscribe();
  }, [loadProfileByEmail]);

  /* ---------------- public helpers ---------------------- */
  const refreshProfile = useCallback(async () => {
    if (session?.user?.email) {
      hasFetchedProfile.current = null;
      await loadProfileByEmail(session.user.email);
    }
  }, [session, loadProfileByEmail]);

  const signOut = useCallback(async () => {
    const { error } = await supabaseClient.auth.signOut();
    if (error && IS_DEV) console.error('Sign‑out error:', error);
    window.location.href = '/';
  }, []);

  /* ---------------- memoised context -------------------- */
  const value = useMemo<AuthContextShape>(
    () => ({
      session,
      sessionStatus,
      isLoading,
      authError,
      profile,
      refreshProfile,
      signOut,
    }),
    [session, sessionStatus, isLoading, authError, profile, refreshProfile, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
